import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link, useBlocker } from 'react-router-dom';
import { ArrowLeft, Save, ImagePlus, X, CheckCircle, Plus, Tag, Target, Zap, Cpu, ClipboardList, Search, Settings, Star, ChevronDown } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import itemService from '../../services/itemService';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { cleanHtmlContent, createQuillImageHandler, processHtmlAndUploadImages } from '../../utils/helpers';
import { toast } from 'react-toastify';
import { PRODUCT_STATUS, PRODUCT_STATUS_CONFIG } from '../../utils/constants';

const lineHeights = ['1.0', '1.2', '1.5', '1.8', '2.0', '2.5', '3.0'];
try {
    const Parchment = Quill.import('parchment');
    const StyleClass = Quill.import('attributors/style/align') || Quill.import('attributors/style/direction');
    
    if (StyleClass && typeof StyleClass.constructor === 'function') {
        const LineHeightStyle = new StyleClass.constructor('lineHeight', 'line-height', {
            scope: Parchment.Scope.BLOCK,
            whitelist: lineHeights,
        });
        Quill.register(LineHeightStyle, true);
    }

    const icons = Quill.import('ui/icons');
    icons['unlink'] = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 1 1 0 10h-2"/><line x1="8" x2="16" y1="12" y2="12"/><line x1="2" x2="22" y1="2" y2="22"/></svg>`;

} catch (error) {
    console.error("Không thể đăng ký LineHeightStyle cho Quill:", error);
}

const AdminProductCreate = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true); 

    const descriptionQuillRef = useRef(null);
    const configQuillRef = useRef(null);

    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [purposes, setPurposes] = useState([]);
    const [sales, setSales] = useState([]);
    
    const [pcComponents, setPcComponents] = useState([]);
    const [compatibilityTags, setCompatibilityTags] = useState([]);
    const [pcComponentId, setPcComponentId] = useState('');
    const [selectedCompatibilityIds, setSelectedCompatibilityIds] = useState([]);

    const [basicInfo, setBasicInfo] = useState({
        name: '', item_code: '', category_id: '', brand_id: '', 
        release_price: '', sale_price: '', flash_sale_price: '', flash_sale_quantity: '', 
        quantity: '', status: 2, description: '', general_config: '', warranty_time: '',
        is_for_rent: false 
    });
    
    const [displayPrices, setDisplayPrices] = useState({
        release_price: '',
        sale_price: '',
        flash_sale_price: ''
    });

    const [rentalPrices, setRentalPrices] = useState([
        { durationLabel: '1-3 ngày', price: '' }
    ]);

    const [selectedPurposes, setSelectedPurposes] = useState([]);
    const [purposeSearch, setPurposeSearch] = useState(''); 
    
    const [selectedSales, setSelectedSales] = useState([]);
    const [images, setImages] = useState([]);

    const [categorySpecs, setCategorySpecs] = useState([]); 
    const [specValues, setSpecValues] = useState({}); 
    const [customSpecs, setCustomSpecs] = useState([]); 
    
    const [isShownInMainPage, setIsShownInMainPage] = useState(true);
    const [isBestSeller, setIsBestSeller] = useState(false);

    // Thay thế Modal bằng Inline Area
    const [showSpecImportArea, setShowSpecImportArea] = useState(false);
    const [specImportText, setSpecImportText] = useState('');
    const [importMode, setImportMode] = useState('tab');

    const [activeSpecId, setActiveSpecId] = useState(null);
    const [specSuggestions, setSpecSuggestions] = useState({}); 
    const [specOptionMap] = useState({});

    const stripHtml = (html) => {
        if (!html) return '';
        return html
            .replace(/<\/(p|div|li|h[1-6])>/gi, ' ') 
            .replace(/<br\s*\/?>/gi, ' ') 
            .replace(/<[^>]*>?/gm, '') 
            .replace(/&nbsp;/g, ' ') 
            .replace(/\s+/g, ' ') 
            .trim();
    };

    const getQuillModules = (quillRef) => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'font': [] }],
                [{ 'align': ['', 'center', 'right', 'justify'] }], 
                [{ 'lineHeight': lineHeights }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }], 
                [{ 'color': [] }, { 'background': [] }],
                
                ['link', 'unlink', 'image'],
                
                ['clean']
            ],
            handlers: {
                lineHeight: function (value) {
                    if (value) {
                        this.quill.format('lineHeight', value);
                    } else {
                        this.quill.format('lineHeight', '1.5'); 
                    }
                },
                unlink: function() {
                    this.quill.format('link', false);
                },
                image: createQuillImageHandler(quillRef, adminStaffService.uploadEditorImage, 'image_item_description')
            }
        },
        keyboard: {
            bindings: {
                tab: {
                    key: 9,
                    handler: function(range, context) {
                        if (context.format.list) return true; 
                        this.quill.insertText(range.index, '    ');
                        this.quill.setSelection(range.index + 4);
                        return false; 
                    }
                },
                asteriskList: {
                    key: ' ', 
                    format: ['list'],
                    handler: function(range, context) {
                        const textBefore = this.quill.getText(range.index - 1, 1);
                        const textStart = this.quill.getText(0, range.index);
                        
                        if ((textBefore === '*' || textBefore === '-') && (textStart.endsWith('\n*') || textStart.endsWith('\n-') || textStart === '*' || textStart === '-')) {
                            this.quill.deleteText(range.index - 1, 1); 
                            this.quill.formatLine(range.index - 1, 1, 'list', 'bullet');
                            return false; 
                        }
                        return true; 
                    }
                }
            }
        },
        clipboard: {
            matchVisual: false
        }
    });

    const descModules = useMemo(() => getQuillModules(descriptionQuillRef), []);
    const configModules = useMemo(() => getQuillModules(configQuillRef), []);

    const getMiniQuillModules = () => ({
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'], 
            [{ 'list': 'bullet' }], 
            ['clean'] 
        ],
        clipboard: { matchVisual: false }
    });
    
    const isSubmittedRef = useRef(false);

    const hasUnsavedChanges = () => {
        return (
            basicInfo.name.trim() !== '' ||
            basicInfo.item_code.trim() !== '' ||
            basicInfo.category_id !== '' ||
            basicInfo.brand_id !== '' ||
            basicInfo.release_price !== '' ||
            basicInfo.quantity !== '' ||
            basicInfo.description !== '' ||
            basicInfo.general_config !== '' ||
            images.length > 0 ||
            customSpecs.length > 0 ||
            Object.values(specValues).some(v => v !== '')
        );
    };
    
    const blocker = useBlocker(() => !isSubmittedRef.current && hasUnsavedChanges());

    // Chặn đóng tab
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            // 👉 SỬA: Phải check thêm điều kiện chưa submit
            if (!isSubmittedRef.current && hasUnsavedChanges()) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [catData, brandData, purposeData, saleData, pcCompData, compatData] = await Promise.all([
                    adminStaffService.getCategories().catch(()=>[]),
                    adminStaffService.getBrands().catch(()=>[]),
                    adminStaffService.getPurposes().catch(()=> []),
                    adminStaffService.getSales().catch(()=> []),
                    adminStaffService.getAllPcComponents().catch(()=> []), 
                    adminStaffService.getAllCompatibilityGroups().catch(()=> []) 
                ]);

                const safeArray = (data) => Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);

                const sortedCategories = safeArray(catData).sort((a, b) => {
                    const nameA = a.deviceCategoryName || a.categoryName || a.name || '';
                    const nameB = b.deviceCategoryName || b.categoryName || b.name || '';
                    return nameA.localeCompare(nameB, 'vi');
                });
                setCategories(sortedCategories);

                const sortedBrands = safeArray(brandData).sort((a, b) => {
                    const nameA = a.brandName || '';
                    const nameB = b.brandName || '';
                    return nameA.localeCompare(nameB, 'vi');
                });
                setBrands(sortedBrands);
                
                const sortedPurposes = safeArray(purposeData).sort((a, b) => (a.purposeOrder || 0) - (b.purposeOrder || 0));
                setPurposes(sortedPurposes);
                
                setSales(safeArray(saleData));
                setPcComponents(safeArray(pcCompData));
                setCompatibilityTags(safeArray(compatData));
            } catch (error) { 
                console.error("Lỗi load dữ liệu:", error); 
            } finally {
                setInitialLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    const fetchCategorySpecs = async (categoryId) => {
        try {
            // 1. Lấy danh sách các thông số gốc của Category
            const response = await adminStaffService.getSpecsByCategory(categoryId);
            let fetchedSpecs = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
            
            fetchedSpecs = fetchedSpecs.sort((a, b) => {
                const orderA = a.specOrder || 0;
                const orderB = b.specOrder || 0;
                if (orderA !== orderB) return orderA - orderB;
                return (a.specId || 0) - (b.specId || 0); 
            });

            setCategorySpecs(fetchedSpecs);
            
            setSpecValues(prev => {
                const newSpecs = { ...prev };
                fetchedSpecs.forEach(spec => {
                    if (newSpecs[spec.specId] === undefined) newSpecs[spec.specId] = '';
                });
                return newSpecs;
            });

        } catch (error) { 
            console.error("Lỗi khi load Specs:", error); 
        }
    };

    useEffect(() => {
        const fetchSpecSuggestions = async () => {
            const categoryId = basicInfo.category_id;
            if (!categoryId) return;

            try {
                // 👉 ĐỔI SANG API MỚI: getItemsByCategoryId thay vì getItemsByMenuId
                const res = await itemService.getItemsByCategoryId(categoryId); 
                
                // Lấy mảng data (Tùy cấu trúc bọc response của backend axiosClient mà có thể là res.data hoặc res)
                const categoryProducts = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);

                console.log(`🔍 [DEBUG - QUÉT DATA] Đã lấy ${categoryProducts.length} sản phẩm từ Category ID: ${categoryId}`);
                
                const newSuggestions = {};
                
                categoryProducts.forEach(p => {
                    if (!p.itemSpecs || !Array.isArray(p.itemSpecs)) return;

                    p.itemSpecs.forEach(spec => {
                        const specId = spec.spec?.specId || spec.specId; 
                        if (!specId) return;

                        const cleanValue = stripHtml(spec.specValue);
                        if (cleanValue) {
                            if (!newSuggestions[specId]) {
                                newSuggestions[specId] = new Set();
                            }
                            newSuggestions[specId].add(cleanValue.trim());
                        }
                    });
                });

                const formattedSuggestions = {};
                Object.keys(newSuggestions).forEach(id => {
                    formattedSuggestions[id] = Array.from(newSuggestions[id]).sort();
                });
                
                console.log("✅ [DEBUG - QUÉT DATA] Mảng gợi ý thu thập được:", formattedSuggestions);
                setSpecSuggestions(formattedSuggestions);
            } catch (error) {
                console.error("Lỗi lấy dữ liệu gợi ý thông số:", error);
            }
        };

        if (!initialLoading && basicInfo.category_id) {
            fetchSpecSuggestions();
        }
    }, [basicInfo.category_id, initialLoading]);

    useEffect(() => {
        // Nếu đã load xong data ban đầu và người dùng đã chọn Category
        if (basicInfo.category_id && !initialLoading) {
            // GỌI HÀM Ở ĐÂY ĐỂ HẾT LỖI "never used"
            fetchCategorySpecs(basicInfo.category_id);
        } else if (!basicInfo.category_id) {
            // Nếu không chọn Category nào thì reset rỗng
            setCategorySpecs([]); 
            setSpecValues({}); 
            setCustomSpecs([]);
        }
    }, [basicInfo.category_id, initialLoading]);

    const handleBasicInfoChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        
        setBasicInfo(prev => ({ ...prev, [name]: val }));
        
        if (name === 'status') {
            const numStatus = Number(value);
            // Thay số 0, 3, 4 bằng constants
            if ([PRODUCT_STATUS.HIDDEN, PRODUCT_STATUS.PRE_ORDER, PRODUCT_STATUS.OUT_OF_STOCK].includes(numStatus)) {
                setIsShownInMainPage(false);
            }
            // Thay số 2 bằng constant
            if (numStatus !== PRODUCT_STATUS.IN_STOCK) {
                setIsBestSeller(false);
            }
        }
    };

    const handlePriceChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value.replace(/\D/g, ''); 
        
        setBasicInfo(prev => ({ ...prev, [name]: numericValue }));
        setDisplayPrices(prev => ({ ...prev, [name]: numericValue ? Number(numericValue).toLocaleString('vi-VN') : '' }));
    };

    const addRentalTier = () => setRentalPrices([...rentalPrices, { durationLabel: '', price: '' }]);
    const updateRentalTier = (index, field, val) => {
        const newTiers = [...rentalPrices];
        if (field === 'price') {
            newTiers[index][field] = val.replace(/\D/g, '');
        } else {
            newTiers[index][field] = val;
        }
        setRentalPrices(newTiers);
    };
    const removeRentalTier = (index) => setRentalPrices(rentalPrices.filter((_, i) => i !== index));

    const handleCheckboxChange = (id, stateArray, setStateArray) => {
        stateArray.includes(id) ? setStateArray(stateArray.filter(i => i !== id)) : setStateArray([...stateArray, id]);
    };

    const handleTagToggle = (tagId) => {
        selectedCompatibilityIds.includes(tagId) ? setSelectedCompatibilityIds(selectedCompatibilityIds.filter(id => id !== tagId)) : setSelectedCompatibilityIds([...selectedCompatibilityIds, tagId]);
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map((file, index) => ({
            file, previewUrl: URL.createObjectURL(file), isMain: images.length === 0 && index === 0 
        }));
        setImages(prev => [...prev, ...newImages]);
    };

    const removeImage = (indexToRemove) => setImages(images.filter((_, index) => index !== indexToRemove));
    const setMainImage = (indexToMain) => setImages(images.map((img, index) => ({ ...img, isMain: index === indexToMain })));
    
    const addCustomSpec = () => setCustomSpecs([...customSpecs, { id: Date.now() + Math.random(), name: '', value: '' }]);
    
    const updateCustomSpec = (index, field, val) => {
        const newSpecs = [...customSpecs];
        newSpecs[index][field] = val;
        setCustomSpecs(newSpecs);
    };
    const removeCustomSpec = (index) => setCustomSpecs(customSpecs.filter((_, i) => i !== index));

    const handleImportSpecs = () => {
        if (!specImportText.trim()) {
            toast.error("Vui lòng dán nội dung vào ô text!");
            return;
        }

        const rawLines = specImportText.split('\n');
        const parsedSpecs = [];
        let currentSpec = null;

        const cleanExcelQuotes = (str) => {
            return str.replace(/^"|"$/g, '').trim();
        };

        if (importMode === 'tab') {
            let rows = [];
            let currentRow = [];
            let currentCell = '';
            let inQuotes = false;
            
            for (let i = 0; i < specImportText.length; i++) {
                const char = specImportText[i];
                const nextChar = specImportText[i + 1];
                
                if (inQuotes) {
                    if (char === '"') {
                        if (nextChar === '"') {
                            currentCell += '"';
                            i++; 
                        } else {
                            inQuotes = false;
                        }
                    } else {
                        currentCell += char;
                    }
                } else {
                    if (char === '"') {
                        inQuotes = true;
                    } else if (char === '\t') {
                        currentRow.push(currentCell);
                        currentCell = '';
                    } else if (char === '\n') {
                        currentRow.push(currentCell);
                        rows.push(currentRow);
                        currentRow = [];
                        currentCell = '';
                    } else if (char !== '\r') {
                        currentCell += char;
                    }
                }
            }
            if (currentCell || currentRow.length > 0) {
                currentRow.push(currentCell);
                rows.push(currentRow);
            }

            rows.forEach(row => {
                if (row.length === 0 || (row.length === 1 && row[0].trim() === '')) return;

                if (row.length > 1) {
                    const namePart = row[0].trim();
                    let valuePart = row.slice(1).join(' ').trim().replace(/^\n+|\n+$/g, '').replace(/\n/g, '<br/>');

                    if (namePart !== '') {
                        currentSpec = { name: namePart, value: valuePart };
                        parsedSpecs.push(currentSpec);
                    } else {
                        if (currentSpec && valuePart !== '') {
                            currentSpec.value += (currentSpec.value ? '<br/>' : '') + valuePart;
                        }
                    }
                } else {
                    let text = row[0].trim().replace(/^\n+|\n+$/g, '').replace(/\n/g, '<br/>');
                    if (text.includes(':') && !currentSpec) {
                        const separatorIndex = text.indexOf(':');
                        const name = text.substring(0, separatorIndex).trim();
                        const value = text.substring(separatorIndex + 1).trim();
                        currentSpec = { name: name, value: value };
                        parsedSpecs.push(currentSpec);
                    } else {
                        if (currentSpec && text !== '') {
                            currentSpec.value += (currentSpec.value ? '<br/>' : '') + text;
                        }
                    }
                }
            });

        } else if (importMode === 'alternate') {
            const lines = rawLines.map(l => cleanExcelQuotes(l)).filter(l => l !== '');
            for (let i = 0; i < lines.length - 1; i += 2) {
                parsedSpecs.push({ name: lines[i], value: lines[i + 1].replace(/\n/g, '<br/>') });
            }
        }

        if (parsedSpecs.length === 0) {
            toast.error("Không tìm thấy cấu trúc dữ liệu hợp lệ! Vui lòng kiểm tra lại định dạng.");
            return;
        }

        const newSpecValues = { ...specValues };
        let newCustomSpecs = customSpecs.filter(s => s.name.trim() !== '' || s.value.trim() !== '');
        
        let updatedCount = 0;
        let addedCount = 0;

        const touchedSpecs = new Set();
        const touchedCustom = new Set();

        parsedSpecs.forEach(parsed => {
            const normalizedName = parsed.name.toLowerCase().trim();
            const value = parsed.value.replace(/\n/g, '<br/>').trim();

            if (!value) return;

            const matchedCategorySpec = categorySpecs.find(s => s.specName.toLowerCase().trim() === normalizedName);
            
            if (matchedCategorySpec) {
                const specId = matchedCategorySpec.specId;
                if (touchedSpecs.has(specId)) {
                    newSpecValues[specId] += '<br/>' + value;
                } else {
                    newSpecValues[specId] = value;
                    touchedSpecs.add(specId); 
                    updatedCount++;
                }
            } else {
                const existingCustomSpecIndex = newCustomSpecs.findIndex(s => s.name.toLowerCase().trim() === normalizedName);
                if (existingCustomSpecIndex !== -1) {
                    const existingSpec = newCustomSpecs[existingCustomSpecIndex];
                    if (touchedCustom.has(existingCustomSpecIndex)) {
                        newCustomSpecs[existingCustomSpecIndex] = { ...existingSpec, value: existingSpec.value + '<br/>' + value };
                    } else {
                        newCustomSpecs[existingCustomSpecIndex] = { ...existingSpec, value: value };
                        touchedCustom.add(existingCustomSpecIndex);
                        updatedCount++;
                    }
                } else {
                    newCustomSpecs.push({ id: Date.now() + Math.random(), name: parsed.name, value: value });
                    touchedCustom.add(newCustomSpecs.length - 1); 
                    addedCount++;
                }
            }
        });

        setSpecValues(newSpecValues);
        setCustomSpecs(newCustomSpecs);

        toast.success(`Import hoàn tất! (Cập nhật: ${updatedCount} | Thêm mới: ${addedCount})`);
        setShowSpecImportArea(false); // Đóng area sau khi import xong
        setSpecImportText('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        const toastId = toast.loading("Đang xử lý dữ liệu và tạo sản phẩm...");

        try {
            const cleanDescription = await processHtmlAndUploadImages(
                basicInfo.description, 
                'image_description', 
                adminStaffService.uploadEditorImage,
                adminStaffService.uploadExternalEditorImage
            );
            
            const cleanGeneralConfig = await processHtmlAndUploadImages(
                basicInfo.general_config, 
                'image_config', 
                adminStaffService.uploadEditorImage,
                adminStaffService.uploadExternalEditorImage
            );

            const finalItemSpecs = [];

            categorySpecs.forEach(spec => {
                const val = specValues[spec.specId] || '';
                if (val !== '') {
                    finalItemSpecs.push({ 
                        specId: Number(spec.specId), 
                        specValue: val, 
                        specOrder: spec.specOrder || 999
                    });
                }
            });

            let maxCatOrder = categorySpecs.reduce((max, s) => Math.max(max, s.specOrder || 0), 0);
            let customOrder = maxCatOrder + 1;

            for (const spec of customSpecs) {
                if (spec.name.trim() !== '' && spec.value.trim() !== '') {
                    let finalSpecId = null;
                    const existingCatSpec = categorySpecs.find(
                        c => c.specName.toLowerCase().trim() === spec.name.toLowerCase().trim()
                    );
                    if (existingCatSpec) {
                        finalSpecId = existingCatSpec.specId;
                    } else {
                        try {
                            maxCatOrder++;
                            const newSpecRes = await adminStaffService.createSpec({
                                categoryId: Number(basicInfo.category_id),
                                specName: spec.name.trim(),
                                isFilterable: false,
                                specOrder: maxCatOrder
                            });
                            finalSpecId = newSpecRes?.data?.specId 
                                ?? newSpecRes?.data?.id 
                                ?? newSpecRes?.specId 
                                ?? newSpecRes?.id 
                                ?? null;
                        } catch (e) { console.error("Lỗi tạo Spec:", e); }
                    }

                    if (!finalSpecId) continue;

                    finalItemSpecs.push({ 
                        specId: finalSpecId,
                        specValue: spec.value.trim(), 
                        specOrder: customOrder++
                    });
                }
            }

            const payload = {
                itemInfo: {
                    itemName: basicInfo.name,
                    itemCode: basicInfo.item_code,
                    itemReleasePrice: basicInfo.release_price ? Number(basicInfo.release_price) : null,
                    itemSalePrice: basicInfo.sale_price ? Number(basicInfo.sale_price) : null,
                    itemFlashSalePrice: basicInfo.flash_sale_price ? Number(basicInfo.flash_sale_price) : null,
                    itemFlashSaleQuantity: basicInfo.flash_sale_quantity ? Number(basicInfo.flash_sale_quantity) : null,
                    itemQuantity: Number(basicInfo.quantity),
                    itemStatus: Number(basicInfo.status),
                    categoryId: Number(basicInfo.category_id),
                    brandId: Number(basicInfo.brand_id),
                    isShownInMainPage: isShownInMainPage,
                    isBestSeller: isBestSeller, 
                    description: cleanHtmlContent(cleanDescription), 
                    generalConfig: cleanHtmlContent(cleanGeneralConfig),
                    warrantyTime: basicInfo.warranty_time,
                    priority: 0,
                    isForRent: basicInfo.is_for_rent,
                    rentalPrices: basicInfo.is_for_rent 
                        ? rentalPrices.map(r => ({ durationLabel: r.durationLabel, price: Number(r.price) || 0 })).filter(r => r.durationLabel.trim() !== '')
                        : []
                },
                purposes: selectedPurposes, 
                sales: selectedSales,        
                itemSpecs: finalItemSpecs, 
                pcComponentId: pcComponentId ? Number(pcComponentId) : null,
                compatibilityIds: selectedCompatibilityIds
            };

            const formData = new FormData();
            formData.append("productData", JSON.stringify(payload)); 
            
            images.forEach(img => {
                formData.append("images", img.file);
                formData.append("isMainFlags", img.isMain);
            });

            await adminStaffService.createNewItem(formData);
            
            isSubmittedRef.current = true; // 👉 THÊM DÒNG NÀY: Cập nhật cờ đồng bộ lập tức
            
            toast.update(toastId, { render: "Tạo sản phẩm thành công!", type: "success", isLoading: false, autoClose: 2000 });
            navigate('/admin/products'); 

        } catch (error) {
            const errorMsg = error.response?.data?.message || error.response?.data || error.message;
            toast.update(toastId, { render: "Lỗi kết nối: " + errorMsg, type: "error", isLoading: false, autoClose: 3000 });
        } 
        finally { setLoading(false); }
    };

    const filteredPurposes = purposes
        .filter(p => p.purposeName.toLowerCase().includes(purposeSearch.toLowerCase()))
        .sort((a, b) => {
            const isASelected = selectedPurposes.includes(a.purposeId);
            const isBSelected = selectedPurposes.includes(b.purposeId);
            if (isASelected && !isBSelected) return -1;
            if (!isASelected && isBSelected) return 1;
            return 0; 
        });

    if (initialLoading) return <div className="text-center py-20 text-tnc-orange font-bold">Đang tải dữ liệu...</div>;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col relative font-inter">
            <style dangerouslySetInnerHTML={{__html: `
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-label::before { content: 'Giãn dòng'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.0"]::before { content: '1.0'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.2"]::before { content: '1.2'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.5"]::before { content: '1.5'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.8"]::before { content: '1.8'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2.0"]::before { content: '2.0'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2.5"]::before { content: '2.5'; }
                .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="3.0"]::before { content: '3.0'; }
                
                .ql-snow .ql-toolbar button.ql-unlink svg {
                    width: 18px;
                    height: 18px;
                }
                .ql-snow .ql-toolbar button.ql-unlink {
                    color: #444;
                }
                .ql-snow .ql-toolbar button.ql-unlink:hover {
                    color: #06c;
                }
            `}} />

            <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm py-4 px-4 sm:px-8 mb-6 w-full">
                <div className="container mx-auto max-w-6xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Link to="/admin/products" className="p-2 bg-gray-100 rounded-md shadow-sm text-gray-500 hover:text-tnc-orange hover:bg-orange-50 transition-colors">
                            <ArrowLeft size={20} />
                        </Link>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                            Thêm Mới Sản Phẩm
                        </h2>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <button 
                            onClick={handleSubmit} 
                            disabled={loading} 
                            className="flex-1 sm:flex-none flex justify-center items-center gap-2 bg-tnc-orange hover:bg-tnc-orange-hover text-white px-6 py-2.5 rounded-md font-bold transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span> : <Save size={20} />} 
                            <span className="hidden sm:inline">LƯU SẢN PHẨM</span>
                            <span className="inline sm:hidden">LƯU</span>
                        </button>
                    </div>
                </div>
            </div>

            <main className="container mx-auto max-w-6xl pb-10 px-4 sm:px-8 flex-grow">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* CỘT TRÁI (COL-2) */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* THÔNG TIN CƠ BẢN */}
                        <div className="bg-gradient-to-b from-orange-50 to-white p-6 rounded-xl shadow-sm border border-orange-200/60">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-orange-200/60 pb-2 flex items-center gap-2">
                                <Tag size={20} className="text-tnc-orange"/> Thông tin cơ bản
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tên sản phẩm *</label>
                                    <input type="text" name="name" value={basicInfo.name} onChange={handleBasicInfoChange} required placeholder="Nhập tên sản phẩm..." className="w-full px-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange bg-white shadow-sm" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Mã sản phẩm (Model)</label>
                                        <input type="text" name="item_code" value={basicInfo.item_code} onChange={handleBasicInfoChange} placeholder="Nhập mã..." className="w-full px-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange font-mono text-blue-600 bg-white shadow-sm" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Loại sản phẩm *</label>
                                        <select name="category_id" value={basicInfo.category_id} onChange={handleBasicInfoChange} required className="w-full px-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange bg-white shadow-sm">
                                            <option value="">-- Chọn loại SP --</option>
                                            {categories.map(cat => (<option key={cat.deviceCategoryId} value={cat.deviceCategoryId}>{cat.deviceCategoryName}</option>))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Hãng sản xuất *</label>
                                        <select name="brand_id" value={basicInfo.brand_id} onChange={handleBasicInfoChange} required className="w-full px-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange bg-white shadow-sm">
                                            <option value="">-- Chọn hãng --</option>
                                            {brands.map(b => (<option key={b.brandId} value={b.brandId}>{b.brandName}</option>))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái bán *</label>
                                        <select name="status" value={basicInfo.status} onChange={handleBasicInfoChange} required className="w-full px-4 py-2 border border-blue-200 rounded-lg outline-none focus:border-tnc-orange font-bold text-blue-700 bg-blue-50 shadow-sm">
                                            {Object.entries(PRODUCT_STATUS_CONFIG).map(([key, config]) => {
                                                const statusValue = Number(key);
                                                let displayText = config.text;
                                                
                                                return (
                                                    <option key={statusValue} value={statusValue}>
                                                        {displayText}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className="md:col-span-3">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Thời gian bảo hành</label>
                                        <input type="text" name="warranty_time" value={basicInfo.warranty_time} onChange={handleBasicInfoChange} placeholder="VD: 12 tháng..." className="w-full px-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange bg-white shadow-sm" />
                                    </div>
                                </div>

                                <div className="bg-orange-50/50 p-4 rounded-lg border border-orange-100 mt-4">
                                    <h4 className="text-sm font-bold text-orange-800 mb-3 flex items-center justify-between border-b border-orange-200 pb-2">
                                        <span className="flex items-center gap-1"><Zap size={16}/> Thiết lập Giá bán</span>
                                    </h4>
                                    
                                    <div className="mb-4 pb-4 border-b border-orange-200/50">
                                        <label className="flex items-center gap-2 cursor-pointer w-max">
                                            <input 
                                                type="checkbox" 
                                                name="is_for_rent" 
                                                checked={basicInfo.is_for_rent} 
                                                onChange={handleBasicInfoChange} 
                                                className="w-4 h-4 accent-tnc-orange rounded cursor-pointer" 
                                            />
                                            <span className="font-bold text-gray-800">Sản phẩm cho thuê</span>
                                        </label>
                                    </div>

                                    {!basicInfo.is_for_rent ? (
                                        // GIAO DIỆN BÁN BÌNH THƯỜNG
                                        <>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                                <div className="w-full">
                                                    <label className="block text-sm font-bold text-gray-700 mb-1">Giá gốc (Bán lẻ) *</label>
                                                    <div className="relative">
                                                        <input 
                                                            type="text" 
                                                            name="release_price" 
                                                            value={displayPrices.release_price} 
                                                            onChange={handlePriceChange} 
                                                            placeholder="Để trống nếu là Giá Liên Hệ" 
                                                            className="w-full pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-gray-800 bg-white shadow-sm" 
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">đ</span>
                                                    </div>
                                                </div>
                                                <div className="w-full">
                                                    <label className="block text-sm font-bold text-gray-700 mb-1">Giá Khuyến mãi (Sale)</label>
                                                    <div className="relative">
                                                        <input 
                                                            type="text" 
                                                            name="sale_price" 
                                                            value={displayPrices.sale_price} 
                                                            onChange={handlePriceChange} 
                                                            placeholder="Nhập nếu có..." 
                                                            className="w-full pl-3 pr-10 py-2.5 border border-blue-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-blue-600 bg-white shadow-sm" 
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 font-bold">đ</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="w-full">
                                                    <label className="flex items-center text-sm font-bold text-tnc-orange mb-1">Giá Flash Sale (Deal sốc)</label>
                                                    <div className="relative">
                                                        <input 
                                                            type="text" 
                                                            name="flash_sale_price" 
                                                            value={displayPrices.flash_sale_price} 
                                                            onChange={handlePriceChange} 
                                                            placeholder="Nhập nếu có..." 
                                                            className="w-full pl-3 pr-10 py-2.5 border border-orange-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-tnc-orange bg-orange-50/30 shadow-sm" 
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-orange-400 font-bold">đ</span>
                                                    </div>
                                                </div>
                                                <div className="w-full">
                                                    <label className="block text-sm font-bold text-tnc-orange mb-1">Giới hạn SL Flash Sale</label>
                                                    <input 
                                                        type="number" 
                                                        name="flash_sale_quantity" 
                                                        value={basicInfo.flash_sale_quantity} 
                                                        onChange={handleBasicInfoChange} 
                                                        onWheel={(e) => e.target.blur()}
                                                        placeholder="Số lượng nếu có..." 
                                                        className="w-full px-3 py-2.5 border border-orange-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-gray-700 bg-white shadow-sm" 
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        // GIAO DIỆN CÁC MỐC GIÁ CHO THUÊ
                                        <div className="space-y-3">
                                            {rentalPrices.map((tier, index) => (
                                                <div key={index} className="flex gap-3 items-start bg-white p-3 rounded-lg border border-orange-200 shadow-sm">
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Mốc thời gian</label>
                                                        <input 
                                                            type="text" 
                                                            placeholder="VD: 1-3 ngày, 4-7 ngày..." 
                                                            value={tier.durationLabel}
                                                            onChange={(e) => updateRentalTier(index, 'durationLabel', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-200 rounded outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange text-sm shadow-sm"
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Giá thuê</label>
                                                        <div className="relative">
                                                            <input 
                                                                type="text" 
                                                                placeholder="Nhập giá..." 
                                                                value={tier.price ? Number(tier.price).toLocaleString('vi-VN') : ''}
                                                                onChange={(e) => updateRentalTier(index, 'price', e.target.value)}
                                                                className="w-full pl-3 pr-8 py-2 border border-gray-200 rounded outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-tnc-orange text-sm shadow-sm"
                                                            />
                                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">đ</span>
                                                        </div>
                                                    </div>
                                                    <button type="button" onClick={() => removeRentalTier(index)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-5">
                                                        <X size={20} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button type="button" onClick={addRentalTier} className="w-full py-2.5 mt-2 border-2 border-dashed border-tnc-orange text-tnc-orange rounded-lg font-bold hover:bg-orange-50/50 text-sm flex items-center justify-center gap-2 transition-colors">
                                                <Plus size={16} /> Thêm mốc giá thuê
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">Giới hạn số lượng đặt hàng *</label>
                                    <input 
                                        type="number" 
                                        name="quantity" 
                                        value={basicInfo.quantity} 
                                        onChange={handleBasicInfoChange} 
                                        onWheel={(e) => e.target.blur()} 
                                        required 
                                        placeholder="0" 
                                        className="w-1/3 px-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange shadow-sm" 
                                    />
                                </div>

                                <div className="mt-4 p-4 border border-blue-200/60 bg-blue-50/50 rounded-lg transition-colors flex flex-col gap-3 shadow-sm">
                                    <label className="flex items-center justify-between cursor-pointer w-full pb-3 border-b border-blue-200/60">
                                        <div className="flex items-center gap-3">
                                            <input 
                                                type="checkbox" 
                                                checked={isShownInMainPage} 
                                                disabled={[PRODUCT_STATUS.HIDDEN, PRODUCT_STATUS.OUT_OF_STOCK, PRODUCT_STATUS.DISCONTINUED].includes(Number(basicInfo.status))}
                                                onChange={(e) => setIsShownInMainPage(e.target.checked)} 
                                                className="w-5 h-5 text-blue-600 rounded disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer" 
                                            />
                                            <div>
                                                <span className="font-bold text-gray-800 block text-sm">Hiển thị lên Trang chủ</span>
                                                <span className="text-xs text-gray-500">Đánh dấu nếu bạn muốn sản phẩm này xuất hiện ở trang chủ.</span>
                                            </div>
                                        </div>
                                    </label>

                                    <label className="flex items-center justify-between cursor-pointer w-full pt-1">
                                        <div className="flex items-center gap-3">
                                            <input 
                                                type="checkbox" 
                                                checked={isBestSeller} 
                                                disabled={![PRODUCT_STATUS.IN_STOCK, PRODUCT_STATUS.PRE_ORDER].includes(Number(basicInfo.status))} 
                                                onChange={(e) => setIsBestSeller(e.target.checked)} 
                                                className="w-5 h-5 accent-tnc-orange rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" 
                                            />
                                            <div>
                                                <span className="font-bold text-gray-800 block text-sm">Sản phẩm Bán chạy (Best Seller)</span>
                                                <span className="text-xs text-gray-500">Nổi bật sản phẩm này trong danh mục Best Seller.</span>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* CẤU HÌNH BUILD PC */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-orange-200/60">
                            <h3 className="text-lg font-bold text-[#002855] mb-4 border-b border-orange-200/60 pb-2 flex items-center justify-between">
                                <div className="flex items-center gap-2"><Cpu size={20} className="text-tnc-orange"/> Cấu hình cho PC Builder</div>
                            </h3>
                            
                            <div className="mb-6">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Sản phẩm này là linh kiện gì? (Tùy chọn)</label>
                                <select 
                                    value={pcComponentId} 
                                    onChange={(e) => setPcComponentId(e.target.value)}
                                    className="w-full border border-gray-200 p-2.5 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange bg-white shadow-sm"
                                >
                                    <option value="">-- Không thuộc bộ Build PC --</option>
                                    {pcComponents.map(comp => (
                                        <option key={comp.componentId} value={comp.componentId}>
                                            [{comp.componentCode}] - {comp.componentName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {pcComponentId && (
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-3">Gắn Tag Tương Thích (Compatibility Tags)</label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {Object.entries(
                                            compatibilityTags.reduce((acc, tag) => {
                                                if (!acc[tag.groupType]) acc[tag.groupType] = [];
                                                acc[tag.groupType].push(tag);
                                                return acc;
                                            }, {})
                                        ).map(([type, tags]) => (
                                            <div key={type} className="border border-orange-200/60 rounded-lg p-3 bg-gradient-to-br from-orange-50 to-white shadow-sm">
                                                <h4 className="font-bold text-xs text-tnc-orange mb-2 uppercase">{type}</h4>
                                                <div className="flex flex-col gap-2">
                                                    {tags.map(tag => (
                                                        <label key={tag.compatibilityId} className="flex items-center gap-2 cursor-pointer hover:bg-orange-100/50 p-1 rounded transition-colors">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={selectedCompatibilityIds.includes(tag.compatibilityId)}
                                                                onChange={() => handleTagToggle(tag.compatibilityId)}
                                                                className="w-4 h-4 accent-tnc-orange cursor-pointer"
                                                            />
                                                            <span className="text-sm font-medium text-gray-700">{tag.groupValue}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* HÌNH ẢNH SẢN PHẨM */}
                        <div className="bg-gradient-to-b from-orange-50 to-white p-6 rounded-xl shadow-sm border border-orange-200/60">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-orange-200/60 pb-2 flex items-center gap-2"><ImagePlus size={20} className="text-tnc-orange"/> Hình ảnh sản phẩm</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                {images.map((img, index) => (
                                    <div key={index} className={`relative rounded-lg border-2 p-1 group ${img.isMain ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'} shadow-sm`}>
                                        <img src={img.previewUrl} alt="preview" className="w-full h-24 object-contain rounded" />
                                        <button onClick={() => removeImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-10 opacity-0 group-hover:opacity-100"><X size={14}/></button>
                                        {!img.isMain && <button onClick={() => setMainImage(index)} className="absolute bottom-1 left-1 right-1 bg-black/70 text-white text-[10px] py-1 rounded text-center hover:bg-black/90 opacity-0 group-hover:opacity-100">Đặt làm ảnh chính</button>}
                                        {img.isMain && <div className="absolute top-1 left-1 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded font-bold">ẢNH CHÍNH</div>}
                                    </div>
                                ))}
                                <label className="border-2 border-dashed border-orange-300 bg-white rounded-lg h-28 flex flex-col items-center justify-center text-orange-500 hover:bg-orange-50 hover:border-tnc-orange hover:text-tnc-orange cursor-pointer transition-colors shadow-sm">
                                    <Plus size={24} className="mb-1" /><span className="text-xs font-bold">Thêm ảnh</span>
                                    <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
                                </label>
                            </div>
                        </div>

                        {/* TÌM KIẾM VÀ HIỂN THỊ MỤC ĐÍCH */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-orange-200/60">
                            <h3 className="text-lg font-bold text-[#002855] mb-4 border-b border-orange-200/60 pb-2 flex items-center gap-2">
                                <Target size={20} className="text-tnc-orange" /> Danh mục / Nhu cầu sử dụng
                            </h3>
                            
                            <div className="relative mb-3">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input 
                                    type="text" 
                                    placeholder="Tìm danh mục..." 
                                    value={purposeSearch}
                                    onChange={(e) => setPurposeSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange shadow-sm bg-white"
                                />
                            </div>

                            <div className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar border border-gray-100 rounded-lg p-2 bg-gray-50/50">
                                {filteredPurposes.length > 0 ? filteredPurposes.map(p => (
                                    <label key={p.purposeId} className={`flex items-center gap-3 p-2 rounded cursor-pointer border transition-colors ${selectedPurposes.includes(p.purposeId) ? 'border-orange-200 bg-orange-50/50' : 'border-transparent hover:border-orange-100 hover:bg-white'}`}>
                                        <input type="checkbox" checked={selectedPurposes.includes(p.purposeId)} onChange={() => handleCheckboxChange(p.purposeId, selectedPurposes, setSelectedPurposes)} className="w-4 h-4 accent-tnc-orange rounded cursor-pointer" />
                                        <span className={`text-sm font-medium ${selectedPurposes.includes(p.purposeId) ? 'text-tnc-orange font-bold' : 'text-gray-700'}`}>{p.purposeName}</span>
                                    </label>
                                )) : <p className="text-sm text-gray-500 text-center py-2">Không tìm thấy nhu cầu phù hợp.</p>}
                            </div>
                        </div>

                        {/* CHỌN KHUYẾN MÃI */}
                        <div className="bg-gradient-to-b from-orange-50 to-white p-6 rounded-xl shadow-sm border border-orange-200/60">
                            <h3 className="text-lg font-bold text-tnc-orange mb-4 border-b border-orange-200/60 pb-2 flex items-center gap-2">
                                <Zap size={20}/> Khuyến mãi
                            </h3>
                            <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                {sales.length > 0 ? sales.map(s => (
                                    <label key={s.saleId} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-colors shadow-sm bg-white ${selectedSales.includes(s.saleId) ? 'border-tnc-orange bg-orange-50/30' : 'border-orange-100 hover:border-orange-300'}`}>
                                        <input type="checkbox" checked={selectedSales.includes(s.saleId)} onChange={() => handleCheckboxChange(s.saleId, selectedSales, setSelectedSales)} className="w-4 h-4 accent-tnc-orange rounded cursor-pointer mt-1 shrink-0" />
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <span className={`text-sm font-bold line-clamp-1 ${selectedSales.includes(s.saleId) ? 'text-tnc-orange' : 'text-gray-800'}`} title={s.saleName}>{s.saleName || 'Chưa có tên'}</span>
                                            <div className="text-xs text-gray-600 mt-1 line-clamp-2 
                                                [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:my-0.5 
                                                [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:my-0.5 
                                                [&_li]:my-0.5 [&_p]:my-0.5 [&_strong]:font-bold [&_em]:italic"
                                            >
                                                {s.description ? (
                                                    <div dangerouslySetInnerHTML={{ __html: s.description.replace(/&nbsp;/g, ' ') }} />
                                                ) : (
                                                    <span className="italic text-gray-400">Không có mô tả</span>
                                                )}
                                            </div>
                                        </div>
                                    </label>
                                )) : <p className="text-sm text-gray-500">Chưa có khuyến mãi.</p>}
                            </div>
                        </div>

                        {/* BÀI VIẾT MÔ TẢ SẢN PHẨM */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-gray-100 pb-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Tag size={20} className="text-tnc-orange" /> Bài viết Mô tả Sản phẩm
                                </div>
                            </h3>
                            <div className="bg-white rounded-xl border border-orange-200/60 focus-within:border-tnc-orange focus-within:ring-1 focus-within:ring-tnc-orange transition-all shadow-sm">
                                <ReactQuill 
                                    ref={descriptionQuillRef}
                                    theme="snow" 
                                    value={basicInfo.description} 
                                    onChange={(content, delta, source) => {
                                        setBasicInfo(prev => ({ ...prev, description: content }));
                                    }}
                                    modules={descModules}
                                    placeholder="Nhập bài viết mô tả chi tiết, đặc điểm nổi bật của sản phẩm..."
                                    className="bg-white [&_.ql-container]:min-h-[400px] [&_.ql-editor]:text-base [&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-t-0 [&_.ql-toolbar]:bg-orange-50/50 [&_.ql-container]:border-none pb-1 rounded-xl overflow-hidden"
                                />
                            </div>
                        </div>

                    </div>

                    {/* ========================================================= */}
                    {/* CỘT PHẢI (COL-1): Chứa Cấu hình chung và Specs            */}
                    {/* ========================================================= */}
                    <div className="lg:col-span-1 space-y-6">
                        
                        {/* KHỐI CẤU HÌNH CHUNG (General Config) */}
                        <div className="bg-gradient-to-b from-orange-50 to-white p-6 rounded-xl shadow-sm border border-orange-200/60 top-24">
                            <div className="flex items-center justify-between mb-4 border-b border-orange-200/60 pb-2">
                                <h3 className="text-lg font-bold text-[#002855] flex items-center gap-2">
                                    <Settings size={20} className="text-tnc-orange"/> Cấu hình chung
                                </h3>
                            </div>
                            
                            <div className="bg-white rounded-xl border border-orange-200/60 focus-within:border-tnc-orange focus-within:ring-1 focus-within:ring-tnc-orange transition-all text-sm shadow-sm">
                                <ReactQuill 
                                    ref={configQuillRef}
                                    theme="snow" 
                                    value={basicInfo.general_config} 
                                    onChange={(content, delta, source) => {
                                        setBasicInfo(prev => ({ ...prev, general_config: content }));
                                    }}
                                    modules={configModules}
                                    className="bg-white [&_.ql-container]:min-h-[200px] [&_.ql-editor]:text-sm [&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-t-0 [&_.ql-toolbar]:bg-orange-50/50 [&_.ql-container]:border-none pb-1 rounded-xl overflow-hidden"
                                />
                            </div>
                            
                            {/* KHỐI THÔNG SỐ KỸ THUẬT NẰM NGAY BÊN DƯỚI CẤU HÌNH CHUNG */}
                            <div className="flex items-center justify-between mt-8 mb-4 border-b border-orange-200/60 pb-2">
                                <h3 className="text-lg font-bold text-[#002855] flex items-center gap-2">
                                    Thông số chi tiết {basicInfo.category_id && <CheckCircle size={18} className="text-green-500" />}
                                </h3>
                                {/* NÚT IMPORT INLINE */}
                                {basicInfo.category_id && (
                                    <button 
                                        onClick={() => setShowSpecImportArea(!showSpecImportArea)}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors shadow-sm ${showSpecImportArea ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-orange-50 text-tnc-orange border border-orange-200 hover:bg-tnc-orange hover:text-white'}`}
                                    >
                                        <ClipboardList size={14} /> {showSpecImportArea ? 'Đóng Import' : 'Import'}
                                    </button>
                                )}
                            </div>

                            {!basicInfo.category_id ? (
                                <div className="text-center py-10 bg-orange-50/50 rounded-lg border border-dashed border-orange-200">
                                    <p className="text-sm font-bold text-tnc-orange">Chọn Loại sản phẩm để hiện thông số.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">

                                    {/* KHU VỰC IMPORT INLINE XUẤT HIỆN Ở ĐÂY */}
                                    {showSpecImportArea && (
                                        <div className="bg-orange-50/50 border border-orange-200 rounded-xl p-4 mb-6 shadow-sm animate-in fade-in slide-in-from-top-2">
                                            <h4 className="font-bold text-sm text-[#002855] mb-3 flex items-center gap-2">
                                                <ClipboardList size={16} className="text-tnc-orange"/> Dán dữ liệu Import
                                            </h4>
                                            
                                            <div className="mb-3">
                                                <label className="block text-xs font-bold text-gray-700 mb-2">Định dạng copy:</label>
                                                <div className="flex flex-col gap-2">
                                                    <label className="flex items-center gap-2 cursor-pointer p-2 border border-orange-200 rounded-lg hover:border-tnc-orange bg-white shadow-sm transition-all">
                                                        <input type="radio" name="importMode" value="tab" checked={importMode === 'tab'} onChange={(e) => setImportMode(e.target.value)} className="w-4 h-4 accent-tnc-orange cursor-pointer" />
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-bold text-gray-800">Chia cột dạng Bảng</span>
                                                            <span className="text-[10px] text-gray-500">Copy từ Excel/Web (ngăn cách bằng Tab)</span>
                                                        </div>
                                                    </label>
                                                    <label className="flex items-center gap-2 cursor-pointer p-2 border border-orange-200 rounded-lg hover:border-tnc-orange bg-white shadow-sm transition-all">
                                                        <input type="radio" name="importMode" value="alternate" checked={importMode === 'alternate'} onChange={(e) => setImportMode(e.target.value)} className="w-4 h-4 accent-tnc-orange cursor-pointer" />
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-bold text-gray-800">Dạng dòng so le</span>
                                                            <span className="text-[10px] text-gray-500">Dòng 1: Tên, Dòng 2: Giá trị</span>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>

                                            <textarea 
                                                value={specImportText}
                                                onChange={(e) => setSpecImportText(e.target.value)}
                                                placeholder={`Dán dữ liệu vào đây...\nVí dụ:\nCông nghệ CPU\tIntel Core i5\nSố nhân\t8`}
                                                className="w-full h-[150px] p-3 border border-orange-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange resize-none font-mono text-xs leading-relaxed shadow-inner bg-white"
                                            ></textarea>

                                            <div className="flex justify-end gap-2 mt-3">
                                                <button onClick={() => setShowSpecImportArea(false)} className="px-3 py-1.5 text-gray-600 text-xs font-bold hover:bg-gray-200 rounded-md transition-colors border border-gray-200 bg-white shadow-sm">
                                                    Hủy
                                                </button>
                                                <button onClick={handleImportSpecs} className="px-4 py-1.5 bg-tnc-orange text-white text-xs font-bold hover:bg-tnc-orange-hover rounded-md transition-colors shadow-md flex items-center gap-1">
                                                    <Plus size={14} /> Chèn dữ liệu
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* DANH SÁCH SPECS GỐC */}
                                    {categorySpecs.map(spec => (
                                        <div key={spec.specId}>
                                            <label className={`flex justify-between items-center text-xs font-bold mb-1 uppercase tracking-wider ${spec.isFilterable ? 'text-tnc-orange' : 'text-gray-600'}`}>
                                                <span className="flex items-center gap-1">
                                                    {spec.specName}
                                                    {spec.isFilterable && <Star size={12} className="fill-tnc-orange text-tnc-orange mb-0.5" title="Thông số quan trọng (Đang hiển thị ở bộ lọc)" />}
                                                </span>
                                            </label>
                                            
                                            {/* === CODE MỚI BẮT ĐẦU TỪ ĐÂY === */}
                                            {spec.isFilterable ? (
                                                <div 
                                                    className="relative w-full"
                                                    onBlur={(e) => {
                                                        if (!e.currentTarget.contains(e.relatedTarget)) {
                                                            setActiveSpecId(null);
                                                        }
                                                    }}
                                                >
                                                    {/* Khung Input */}
                                                    <div className={`flex items-center w-full border rounded-lg transition-all shadow-sm ${activeSpecId === spec.specId ? 'border-tnc-orange ring-1 ring-tnc-orange bg-white' : 'border-orange-200 bg-orange-50/30'}`}>
                                                        <input 
                                                            type="text"
                                                            value={stripHtml(specValues[spec.specId] || '')} 
                                                            onChange={(e) => {
                                                                setSpecValues(prev => ({...prev, [spec.specId]: e.target.value}));
                                                                setActiveSpecId(spec.specId); 
                                                            }}
                                                            onFocus={() => setActiveSpecId(spec.specId)}
                                                            placeholder="Nhập hoặc chọn giá trị..."
                                                            className="w-full px-3 py-2.5 outline-none bg-transparent text-sm font-medium text-gray-800"
                                                        />
                                                        <button 
                                                            type="button" 
                                                            tabIndex="-1"
                                                            onMouseDown={(e) => e.preventDefault()} 
                                                            className="px-2 h-full py-2.5 flex items-center justify-center text-gray-400 hover:text-tnc-orange transition-colors outline-none cursor-pointer"
                                                            onClick={() => setActiveSpecId(activeSpecId === spec.specId ? null : spec.specId)}
                                                        >
                                                            <ChevronDown size={16} className={`transition-transform duration-200 ${activeSpecId === spec.specId ? 'rotate-180 text-tnc-orange' : ''}`} />
                                                        </button>
                                                    </div>

                                                    {/* Menu Dropdown Gợi ý */}
                                                    {activeSpecId === spec.specId && (
                                                        <ul className="absolute z-[60] w-full mt-1.5 bg-white border border-orange-100 rounded-lg shadow-xl max-h-52 overflow-y-auto custom-scrollbar py-1.5 text-sm animate-in fade-in slide-in-from-top-2 duration-200">
                                                            {(() => {
                                                                const currentText = stripHtml(specValues[spec.specId] || '').toLowerCase().trim();
                                                                
                                                                const scrapedSuggestions = specSuggestions[spec.specId] || [];
                                                                const apiOptions = specOptionMap[spec.specId] || [];
                                                                
                                                                const suggestions = Array.from(new Set([
                                                                    ...apiOptions.map(String), 
                                                                    ...scrapedSuggestions.map(String)
                                                                ]));

                                                                const isExactMatch = suggestions.some(s => s.toLowerCase().trim() === currentText);
                                                                const optionsToShow = (currentText === '' || isExactMatch) 
                                                                    ? suggestions 
                                                                    : suggestions.filter(val => val.toLowerCase().includes(currentText));

                                                                if (optionsToShow.length === 0) {
                                                                    return <li className="px-3 py-2.5 text-gray-400 italic text-center">Nhập để tạo giá trị mới...</li>;
                                                                }

                                                                return optionsToShow.map((val, idx) => (
                                                                    <li 
                                                                        key={idx} 
                                                                        onMouseDown={(e) => {
                                                                            e.preventDefault(); 
                                                                            setSpecValues(prev => ({...prev, [spec.specId]: val}));
                                                                            setActiveSpecId(null);
                                                                        }}
                                                                        className={`px-3 py-2.5 cursor-pointer hover:bg-orange-50 transition-colors font-medium border-b border-gray-50 last:border-0 ${currentText === val.toLowerCase().trim() ? 'text-tnc-orange bg-orange-50/50' : 'text-gray-700'}`}
                                                                    >
                                                                        {val}
                                                                    </li>
                                                                ));
                                                            })()}
                                                        </ul>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="w-full border rounded-lg transition-colors shadow-sm border-gray-200 focus-within:border-tnc-orange">
                                                    <ReactQuill 
                                                        theme="snow"
                                                        value={specValues[spec.specId] || ''} 
                                                        onChange={(content) => setSpecValues(prev => ({...prev, [spec.specId]: content}))} 
                                                        modules={getMiniQuillModules()}
                                                        className="bg-white [&_.ql-container]:min-h-[42px] [&_.ql-editor]:text-sm [&_.ql-editor]:p-2 [&_.ql-toolbar]:py-1 [&_.ql-toolbar]:px-2 [&_.ql-toolbar]:bg-orange-50/30 [&_.ql-container]:border-none [&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-t-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-orange-200/60"
                                                    />
                                                </div>
                                            )}
                                            {/* === KẾT THÚC CODE MỚI === */}
                                            
                                        </div>
                                    ))}
                                    
                                    {/* Danh sách các Spec vừa được thêm (Custom Specs chờ lưu) */}
                                    {customSpecs.map((custom, index) => (
                                        <div key={custom.id || index} className="flex gap-2 items-start bg-orange-50/50 p-2 rounded-lg border border-orange-200 shadow-sm relative">
                                            <div className="flex-1 space-y-2">
                                                <input type="text" placeholder="Tên thông số" value={custom.name} onChange={(e) => updateCustomSpec(index, 'name', e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs outline-none focus:border-tnc-orange font-bold shadow-sm bg-white" />
                                                
                                                <div className="w-full border border-gray-200 rounded overflow-hidden focus-within:border-tnc-orange transition-colors shadow-sm">
                                                    <ReactQuill 
                                                        theme="snow"
                                                        placeholder="Giá trị..."
                                                        value={custom.value} 
                                                        onChange={(content) => updateCustomSpec(index, 'value', content)} 
                                                        modules={getMiniQuillModules()}
                                                        className="bg-white [&_.ql-container]:min-h-[40px] [&_.ql-editor]:text-sm [&_.ql-editor]:p-2 [&_.ql-toolbar]:py-1 [&_.ql-toolbar]:bg-gray-50 [&_.ql-container]:border-none [&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-t-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-gray-200"
                                                    />
                                                </div>
                                            </div>
                                            <button onClick={() => removeCustomSpec(index)} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1 rounded transition-colors mt-1"><X size={16}/></button>
                                        </div>
                                    ))}

                                    <button onClick={addCustomSpec} className="w-full py-2.5 mt-4 border-2 border-dashed border-tnc-orange text-tnc-orange rounded-lg font-bold hover:bg-orange-50 text-sm flex items-center justify-center gap-2 transition-colors">
                                        <Plus size={16}/> Thêm thông số mới
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            {/* ĐÃ XÓA MODAL z-[100] CŨ Ở ĐÂY */}

            {blocker.state === 'blocked' && (
                <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-2">Rời khỏi trang?</h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Bạn có thay đổi chưa được lưu. Nếu rời đi, các thay đổi này sẽ bị mất.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => blocker.reset()} className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-lg border border-gray-200">
                                Ở lại
                            </button>
                            <button onClick={() => blocker.proceed()} className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg">
                                Rời đi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminProductCreate;